import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Zap, Timer, Trophy, RotateCcw, Target } from 'lucide-react';

const ReactionGame = () => {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [time, setTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [level, setLevel] = useState(1);
  const [reactionTime, setReactionTime] = useState(0);
  const [isWaiting, setIsWaiting] = useState(false);
  const [rounds, setRounds] = useState(0);
  const [totalReactionTime, setTotalReactionTime] = useState(0);
  
  const gameLoopRef = useRef(null);
  const startTimeRef = useRef(null);
  const waitTimeRef = useRef(null);

  const startGame = () => {
    setIsPlaying(true);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setRounds(0);
    setTotalReactionTime(0);
    setReactionTime(0);
    setIsWaiting(false);
    startRound();
  };

  const resetGame = () => {
    setIsPlaying(false);
    setGameOver(false);
    setScore(0);
    setTime(0);
    setLevel(1);
    setRounds(0);
    setTotalReactionTime(0);
    setReactionTime(0);
    setIsWaiting(false);
  };

  const startRound = () => {
    setIsWaiting(true);
    const waitTime = Math.random() * 3000 + 1000; // 1-4 seconds
    waitTimeRef.current = setTimeout(() => {
      setIsWaiting(false);
      startTimeRef.current = Date.now();
    }, waitTime);
  };

  const handleClick = () => {
    if (!isPlaying || gameOver || isWaiting) return;

    const endTime = Date.now();
    const reaction = endTime - startTimeRef.current;
    
    setReactionTime(reaction);
    setTotalReactionTime(prev => prev + reaction);
    setRounds(prev => prev + 1);
    
    // Calculate score based on reaction time
    let points = 0;
    if (reaction < 100) points = 100;
    else if (reaction < 200) points = 80;
    else if (reaction < 300) points = 60;
    else if (reaction < 500) points = 40;
    else if (reaction < 1000) points = 20;
    else points = 10;
    
    setScore(prev => prev + points);
    
    // Level up every 5 rounds
    if ((rounds + 1) % 5 === 0) {
      setLevel(prev => prev + 1);
    }
    
    // Check if game should end
    if (rounds + 1 >= 20) {
      setGameOver(true);
      setIsPlaying(false);
      if (score + points > highScore) {
        setHighScore(score + points);
      }
    } else {
      // Start next round after a delay
      setTimeout(startRound, 1000);
    }
  };

  useEffect(() => {
    let interval;
    if (isPlaying && !gameOver) {
      interval = setInterval(() => {
        setTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, gameOver]);

  useEffect(() => {
    return () => {
      if (waitTimeRef.current) {
        clearTimeout(waitTimeRef.current);
      }
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const averageReactionTime = rounds > 0 ? Math.round(totalReactionTime / rounds) : 0;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="cyber-card p-6 mb-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div className="flex items-center justify-center space-x-2">
            <Zap className="h-6 w-6 text-neon-blue" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.score')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-blue">{score}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Trophy className="h-6 w-6 text-neon-green" />
            <div>
              <p className="text-sm text-gray-400 font-tech">High Score</p>
              <p className="text-2xl font-cyber font-bold text-neon-green">{highScore}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Timer className="h-6 w-6 text-neon-pink" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.time')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-pink">{formatTime(time)}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <Target className="h-6 w-6 text-neon-purple" />
            <div>
              <p className="text-sm text-gray-400 font-tech">{t('game.level')}</p>
              <p className="text-2xl font-cyber font-bold text-neon-purple">{level}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-center space-x-2">
            <RotateCcw className="h-6 w-6 text-neon-yellow" />
            <button
              onClick={resetGame}
              className="cyber-button text-sm px-4 py-2"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Game Instructions */}
      {!isPlaying && !gameOver && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-6 mb-8 text-center"
        >
          <h3 className="text-2xl font-cyber font-bold text-white mb-4">
            Reaction Speed Test
          </h3>
          <p className="text-gray-300 font-tech mb-6">
            Click as fast as you can when the target appears! Test your reflexes and reaction time.
          </p>
          <button
            onClick={startGame}
            className="cyber-button text-lg px-8 py-3"
          >
            Start Game
          </button>
        </motion.div>
      )}

      {/* Game Area */}
      {isPlaying && !gameOver && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center mb-6">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Round {rounds + 1} of 20
            </h4>
            
            {/* Target Area */}
            <div className="flex justify-center mb-6">
              <motion.div
                className={`w-32 h-32 rounded-full cursor-pointer flex items-center justify-center text-white font-cyber font-bold text-xl ${
                  isWaiting 
                    ? 'bg-gray-600' 
                    : 'bg-neon-green hover:bg-neon-green/80 animate-pulse'
                }`}
                onClick={handleClick}
                whileHover={!isWaiting ? { scale: 1.1 } : {}}
                whileTap={!isWaiting ? { scale: 0.9 } : {}}
              >
                {isWaiting ? 'Wait...' : 'CLICK!'}
              </motion.div>
            </div>
            
            {/* Last Reaction Time */}
            {reactionTime > 0 && (
              <div className="text-center">
                <p className="text-gray-400 font-tech">Last Reaction Time:</p>
                <p className="text-2xl font-cyber font-bold text-neon-blue">{reactionTime}ms</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Game Stats */}
      {isPlaying && (
        <div className="cyber-card p-6 mb-8">
          <div className="text-center">
            <h4 className="text-xl font-cyber font-bold text-white mb-4">
              Game Statistics
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-gray-400 font-tech">Rounds</p>
                <p className="text-2xl font-cyber font-bold text-neon-blue">{rounds}/20</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Avg Reaction</p>
                <p className="text-2xl font-cyber font-bold text-neon-green">{averageReactionTime}ms</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 font-tech">Level</p>
                <p className="text-2xl font-cyber font-bold text-neon-pink">{level}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Game Over Message */}
      {gameOver && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="cyber-card p-6 mb-8 text-center border-neon-green/50"
        >
          <h3 className="text-3xl font-cyber font-bold text-neon-green mb-4">
            Game Complete! 🎯
          </h3>
          <p className="text-gray-300 font-tech mb-4">
            Final Score: {score} | Average Reaction: {averageReactionTime}ms | Time: {formatTime(time)}
          </p>
          <div className="flex justify-center space-x-4">
            <button
              onClick={startGame}
              className="cyber-button text-lg px-6 py-3"
            >
              Play Again
            </button>
            <button
              onClick={resetGame}
              className="cyber-button text-lg px-6 py-3 border-neon-green text-neon-green hover:bg-neon-green hover:text-dark-300"
            >
              Reset
            </button>
          </div>
        </motion.div>
      )}

      {/* Instructions */}
      <div className="cyber-card p-6">
        <div className="text-center">
          <h4 className="text-xl font-cyber font-bold text-white mb-4">
            How to Play
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300 font-tech">
            <div>
              <p className="font-bold text-neon-blue mb-2">Objective</p>
              <p>Click the target as fast as possible when it appears</p>
            </div>
            <div>
              <p className="font-bold text-neon-green mb-2">Scoring</p>
              <p>Faster reactions = higher points</p>
            </div>
            <div>
              <p className="font-bold text-neon-pink mb-2">Rounds</p>
              <p>20 rounds per game</p>
            </div>
            <div>
              <p className="font-bold text-neon-yellow mb-2">Strategy</p>
              <p>Stay focused and be ready to click!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReactionGame;
